// @ts-strict-ignore
import { useTheme } from "@emotion/react";
import { colorProfileMapper, type AviaryColors } from "aviary-tokens";

import type { AviarySize } from "@aviary/types";

import * as styles from "./Badge.styles";

type BadgeColors = Extract<AviaryColors, "success" | "system" | "danger" | "highlight">;

interface Props {
  /**
   * Specify the count displayed
   */
  count?: number;
  /**
   * Adds a border for the top nav.
   * Used to differentiate a circular icon button from the Badge when used together
   */
  isBordered?: boolean;
  /**
   * Color intention
   */
  isColor?: BadgeColors;
  /**
   * Displays badge inline
   * @default false
   */
  isInline?: boolean;
  /**
   * Specifies the size of Badge
   * @default "medium"
   */
  size?: Extract<AviarySize, "small" | "medium">;
  /**
   * Specify if the button is using the "light" profile, has a white background with dark text. To be used on dark backgrounds only
   * @default false
   */
  isLight?: boolean;
  /**
   * Specify if the button is muted, has a light background with dark text.
   * @default false
   */
  isMuted?: boolean;
}

/**
 * Documentation:
 * https://aviary.docs.fullscript.cloud/components/display/Badge
 */
const Badge = ({
  isColor = "success",
  count,
  isBordered = false,
  isInline = false,
  size = "medium",
  isLight = false,
  isMuted = false,
  ...rest
}: Props) => {
  const currentTheme = useTheme();
  const themeColors = colorProfileMapper(currentTheme);
  const isNumberAboveZero = Number.isInteger(count) && count >= 0;

  const badgeStyles = [
    styles.badgeBase,
    size && styles.badgeSize[size],
    isNumberAboveZero && styles.badgeSize.large,
    isInline && styles.inline,
    isBordered && styles.bordered,
    styles.countColors(themeColors[isColor]),
    isColor === "system" && styles.systemBadge,
    isLight && styles.lightBadge,
    isMuted && styles.systemBadgeMuted,
  ];

  const renderCountDisplay = () => {
    if (!isNumberAboveZero) return null;

    const countDisplay = count > 99 ? "99" : count;

    return <div css={styles.textWrapper}>{countDisplay}</div>;
  };

  return (
    <div css={badgeStyles} data-testid="aviary-badge" {...rest}>
      {renderCountDisplay()}
    </div>
  );
};

export { Badge, type BadgeColors, type Props };
